import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import {
  HomePageStarredEntities,
  HomePageRecentlyVisited,
  HomePageTopVisited,
} from '@backstage/plugin-home';
import { SearchContextProvider, SearchBar } from '@backstage/plugin-search-react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import CodeIcon from '@material-ui/icons/Code';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AppsIcon from '@material-ui/icons/Apps';
import BuildIcon from '@material-ui/icons/Build';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import SecurityIcon from '@material-ui/icons/Security';
import AssessmentIcon from '@material-ui/icons/Assessment';

const NTT_RED = '#E4002B';
const NTT_NAVY = '#001433';
const NTT_BLUE = '#003087';

const quickLinks = [
  { label: 'GitLab',     desc: 'Código fuente y pipelines',  href: 'https://umane.emeal.nttdata.com/git', icon: <CodeIcon />,         bg: 'linear-gradient(135deg,#FC6D26,#E24329)' },
  { label: 'Catalog',    desc: 'Servicios y componentes',    href: '/catalog',                            icon: <AppsIcon />,          bg: `linear-gradient(135deg,${NTT_BLUE},${NTT_NAVY})` },
  { label: 'Graph',      desc: 'Dependencias y relaciones',  href: '/catalog-graph',                      icon: <AccountTreeIcon />,   bg: 'linear-gradient(135deg,#7B2D8B,#4A1060)' },
  { label: 'Scaffolder', desc: 'Crear nuevos proyectos',     href: '/create',                             icon: <BuildIcon />,         bg: 'linear-gradient(135deg,#0078D4,#005A9E)' },
  { label: 'APIs',       desc: 'Explorar APIs disponibles',  href: '/api-docs',                           icon: <CloudQueueIcon />,    bg: 'linear-gradient(135deg,#00B294,#007A65)' },
  { label: 'TechDocs',   desc: 'Documentación técnica',      href: '/docs',                               icon: <LibraryBooksIcon />,  bg: 'linear-gradient(135deg,#F7941D,#E07000)' },
  { label: 'ArgoCD',     desc: 'Despliegues y estado CD',    href: 'https://10.1.1.16',                   icon: <TrackChangesIcon />,  bg: 'linear-gradient(135deg,#EF5350,#C62828)' },
  { label: 'Grafana',    desc: 'Métricas y observabilidad',  href: 'https://observabilityandsrenttdata.grafana.net', icon: <AssessmentIcon />, bg: 'linear-gradient(135deg,#F46800,#FF8C42)' },
  { label: 'Security',   desc: 'Auditorías y alertas',       href: '/catalog?filters[kind]=component',    icon: <SecurityIcon />,      bg: 'linear-gradient(135deg,#546E7A,#263238)' },
];

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    background: theme.palette.background.default,
  },
  hero: {
    background: NTT_NAVY,
    padding: theme.spacing(7, 6, 6),
    position: 'relative',
    overflow: 'hidden',
    borderBottom: `3px solid ${NTT_RED}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -80,
      right: -80,
      width: 340,
      height: 340,
      background: 'rgba(0,48,135,0.5)',
      borderRadius: '50%',
      filter: 'blur(80px)',
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -60,
      right: 120,
      width: 200,
      height: 200,
      background: 'rgba(228,0,43,0.08)',
      borderRadius: '50%',
      filter: 'blur(60px)',
      pointerEvents: 'none',
    },
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 680,
  },
  heroEyebrow: {
    color: NTT_RED,
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: 18,
      height: 2,
      background: NTT_RED,
      borderRadius: 2,
    },
  },
  heroTitle: {
    color: '#FFFFFF',
    fontWeight: 800,
    fontSize: '2.4rem',
    lineHeight: 1.15,
    marginBottom: theme.spacing(0.75),
    letterSpacing: '-0.02em',
  },
  heroName: {
    color: '#FFFFFF',
    opacity: 0.75,
    fontWeight: 400,
    fontSize: '0.95rem',
    marginBottom: theme.spacing(3.5),
  },
  searchWrap: {
    maxWidth: 520,
    '& .MuiPaper-root': {
      background: 'rgba(255,255,255,0.06)',
      border: '1.5px solid rgba(255,255,255,0.15)',
      borderRadius: 10,
      boxShadow: 'none',
      transition: 'border-color 0.2s, background 0.2s',
      '&:hover, &:focus-within': {
        background: 'rgba(255,255,255,0.10)',
        borderColor: 'rgba(255,255,255,0.30)',
      },
    },
    '& .MuiInputBase-root': { color: '#fff' },
    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' },
    '& svg': { color: 'rgba(255,255,255,0.5) !important' },
    '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.5)' },
  },
  body: {
    padding: theme.spacing(5, 5, 6),
  },
  sectionLabel: {
    fontSize: '0.67rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  quickCard: {
    borderRadius: 14,
    border: `1.5px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`,
    boxShadow: 'none',
    height: '100%',
    background: theme.palette.background.paper,
    transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 10px 28px rgba(0,20,50,0.10)',
      borderColor: NTT_RED,
    },
  },
  quickCardInner: {
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    height: '100%',
  },
  quickCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': { fontSize: 21, color: '#fff !important' },
  },
  quickCardLabel: {
    fontWeight: 700,
    fontSize: '0.88rem',
    color: theme.palette.text.primary,
  },
  quickCardDesc: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: 1,
  },
}));

const useGreeting = () => {
  const identityApi = useApi(identityApiRef);
  const [name, setName] = useState('');

  useEffect(() => {
    identityApi.getProfileInfo().then(p => {
      const first = (p.displayName ?? '').split(' ')[0];
      setName(first);
    });
  }, [identityApi]);

  const h = new Date().getHours();
  const saludo = h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches';
  return { saludo, name };
};

export const HomePage = () => {
  const classes = useStyles();
  const { saludo, name } = useGreeting();

  return (
    <div className={classes.root}>

      {/* ── HERO ── */}
      <div className={classes.hero}>
        <div className={classes.heroContent}>
          <Typography className={classes.heroEyebrow}>
            Portal de Arquitectura Digital · NTT Data Colombia
          </Typography>
          <Typography className={classes.heroTitle}>
            {saludo}{name ? ',' : '!'} {name && <span>{name}!</span>}
          </Typography>
          <Typography className={classes.heroName}>
            Tu punto de acceso a servicios, APIs, documentación y pipelines.
          </Typography>
          <div className={classes.searchWrap}>
            <SearchContextProvider>
              <SearchBar />
            </SearchContextProvider>
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className={classes.body}>
        <Grid container spacing={4}>

          {/* Quick access */}
          <Grid item xs={12}>
            <Typography className={classes.sectionLabel}>Accesos rápidos</Typography>
            <Grid container spacing={2}>
              {quickLinks.map(link => (
                <Grid item xs={6} sm={4} md={3} key={link.label}>
                  <Card className={classes.quickCard}>
                    <CardActionArea
                      className={classes.quickCardInner}
                      component="a"
                      href={link.href}
                    >
                      <div className={classes.quickCardIcon} style={{ background: link.bg }}>
                        {link.icon}
                      </div>
                      <div>
                        <Typography className={classes.quickCardLabel}>{link.label}</Typography>
                        <Typography className={classes.quickCardDesc}>{link.desc}</Typography>
                      </div>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Widgets */}
          <Grid item xs={12} md={4}>
            <Typography className={classes.sectionLabel}>Favoritos</Typography>
            <HomePageStarredEntities />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography className={classes.sectionLabel}>Visitados recientemente</Typography>
            <HomePageRecentlyVisited />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography className={classes.sectionLabel}>Más visitados</Typography>
            <HomePageTopVisited />
          </Grid>

        </Grid>
      </div>
    </div>
  );
};
